import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import api, { mediaUrl } from '../lib/api';
import { useWS } from '../context/WSContext';
import { useLightbox } from '../context/LightboxContext';
import { motion } from 'framer-motion';
import { Megaphone, Download, FileText, FileArchive, Package, ExternalLink, Loader2, Pin, Eye as EyeIcon, PlayCircle } from 'lucide-react';

function fmtDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' }) + ' · ' +
      d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
}

const KIND_META = {
  pdf: { icon: FileText, label: 'PDF', color: 'text-red-400', action: 'Visualizza PDF' },
  zip: { icon: FileArchive, label: 'ZIP', color: 'text-purple-400', action: 'Scarica ZIP' },
  apk: { icon: Package, label: 'APK', color: 'text-green-400', action: 'Scarica Applicazione' },
  doc: { icon: FileText, label: 'DOC', color: 'text-blue-400', action: 'Scarica documento' },
  other: { icon: FileText, label: 'File', color: 'text-white/80', action: 'Scarica' },
};

function formatSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function AttachmentBlock({ att, onDownload, onOpenImage }) {
  if (!att) return null;
  if (att.kind === 'image') {
    return (
      <button
        type="button"
        onClick={() => onOpenImage(mediaUrl(att.url), att.filename || '')}
        className="block w-full bg-[var(--bg-elevated)] cursor-zoom-in"
        data-testid={`bc-image-${att.file_id}`}
      >
        <img src={mediaUrl(att.url)} alt="" className="w-full max-h-[420px] object-cover" />
      </button>
    );
  }
  if (att.kind === 'video') {
    return (
      <div className="w-full bg-black">
        <video
          controls
          preload="metadata"
          className="w-full max-h-[420px] bg-black"
          src={mediaUrl(att.url)}
          data-testid={`bc-video-${att.file_id}`}
        />
      </div>
    );
  }
  const meta = KIND_META[att.kind] || KIND_META.other;
  const Icon = meta.icon;
  const isPdf = att.kind === 'pdf';
  return (
    <div className="px-4 py-2 space-y-2">
      <button
        onClick={() => onDownload(att)}
        data-testid={`bc-attach-${att.file_id}`}
        className="w-full flex items-center gap-3 p-3 rounded-2xl bg-[var(--bg-elevated)] border border-[var(--border-subtle)] hover:border-[var(--gold)]/40 transition-colors text-left group"
      >
        <div className="w-11 h-11 rounded-xl bg-[var(--gold-soft)] border border-[var(--gold)]/20 flex items-center justify-center flex-shrink-0">
          <Icon className={`w-5 h-5 ${meta.color}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-widest text-white/50 font-bold">{meta.label}</span>
            <span className="text-[10px] text-white/40">{formatSize(att.size)}</span>
          </div>
          <div className="text-sm text-white truncate mt-0.5">{att.filename}</div>
        </div>
        <div className="w-10 h-10 rounded-full bg-[var(--gold)] text-black flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform" style={{ boxShadow: '0 4px 12px rgba(212,175,55,0.3)' }}>
          {isPdf ? <EyeIcon className="w-4 h-4" strokeWidth={2.4} /> : <Download className="w-4 h-4" strokeWidth={2.4} />}
        </div>
      </button>
      {isPdf && (
        <a
          href={mediaUrl(att.url)}
          target="_blank"
          rel="noopener noreferrer"
          className="block text-center text-xs text-[var(--gold)] hover:underline"
          data-testid={`bc-pdf-view-${att.file_id}`}
        >
          Visualizza PDF nel browser
        </a>
      )}
    </div>
  );
}

export default function Comunicazioni() {
  const [items, setItems] = useState(null);
  const { subscribe } = useWS();
  const { open: openImage } = useLightbox();

  const load = () => api.get('/broadcasts').then(({ data }) => setItems(data)).catch(() => setItems([]));

  useEffect(() => {
    load();
    localStorage.setItem('gtc_last_broadcast_seen', new Date().toISOString());
  }, []);

  useEffect(() => {
    const unsub = subscribe((d) => { if (d.type === 'broadcast') load(); });
    return unsub;
    // eslint-disable-next-line
  }, []);

  const downloadAttachment = async (att) => {
    try {
      const { data } = await api.get(att.url, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([data], { type: att.content_type }));
      const a = document.createElement('a');
      a.href = url;
      a.download = att.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      window.open(mediaUrl(att.url), '_blank');
    }
  };

  const openDetail = (b) => {
    api.post(`/broadcasts/${b.id}/view`).catch(() => {});
  };

  return (
    <Layout title="Comunicazioni Importanti" showBack backTo="/home">
      <div className="px-4 pt-4 max-w-md mx-auto space-y-4 pb-8">
        {items === null && (
          <div className="flex items-center justify-center py-16 text-white/40">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        )}
        {items?.length === 0 && (
          <div className="text-center py-16 text-white/50 text-sm">Nessuna comunicazione al momento.</div>
        )}
        {items?.map((b, i) => {
          // Merge single attachment and multi attachments
          const atts = b.attachments && b.attachments.length ? b.attachments : (b.attachment ? [b.attachment] : []);
          return (
            <motion.article
              key={b.id}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.03 * i }}
              onClick={() => openDetail(b)}
              data-testid={`broadcast-card-${b.id}`}
              className="card-surface overflow-hidden"
            >
              <div className="px-4 pt-4 pb-2 flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-[var(--gold-soft)] border border-[var(--gold)]/30 flex items-center justify-center">
                  <Megaphone className="w-4 h-4 text-[var(--gold)]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-[var(--gold)] flex items-center gap-1">
                    {b.is_pinned && <Pin className="w-3 h-3" />} Comunicazione ufficiale
                  </div>
                  <div className="text-[10px] text-white/40">{fmtDate(b.created_at)}</div>
                </div>
              </div>

              {/* First image/video shown big at top */}
              {atts.length > 0 && (atts[0].kind === 'image' || atts[0].kind === 'video') && (
                <AttachmentBlock att={atts[0]} onDownload={downloadAttachment} onOpenImage={openImage} />
              )}

              <div className="px-4 py-3 space-y-3">
                {b.title && <h3 className="font-heading text-lg font-medium text-white leading-tight">{b.title}</h3>}
                {b.message && <div className="text-sm text-white/85 leading-relaxed whitespace-pre-wrap break-words">{b.message}</div>}
                {b.link_url && (
                  <a
                    href={b.link_url}
                    target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-[var(--gold)] text-sm hover:underline break-all"
                  >
                    <ExternalLink className="w-3.5 h-3.5" /> {b.link_url}
                  </a>
                )}
              </div>

              {/* Remaining attachments below */}
              {atts.slice(atts.length > 0 && (atts[0].kind === 'image' || atts[0].kind === 'video') ? 1 : 0).map((att, idx) => (
                <div key={att.file_id + idx}>
                  {(att.kind === 'image' || att.kind === 'video') ? (
                    <AttachmentBlock att={att} onDownload={downloadAttachment} onOpenImage={openImage} />
                  ) : (
                    <AttachmentBlock att={att} onDownload={downloadAttachment} onOpenImage={openImage} />
                  )}
                </div>
              ))}
            </motion.article>
          );
        })}
      </div>
    </Layout>
  );
}
